import { Injectable } from '@angular/core';
import { TagPair } from '~app/shared/map/layer/layer-definitions/T14';
import { NeveMeasurementsModule } from './neve-measurements.module';
import { assign, interpret, createMachine, spawn, send } from 'xstate';
import { getFullTag, NeveMeasurementsTagService } from './neve-measurements-tag.service';
import { TagEvent, TagInitializedEvent, TagServiceContext } from './neve-measurements-tag.interface';
import { concat, from, Observable, of } from 'rxjs';
import { filter, map, scan, switchMap } from 'rxjs/operators';
import { TagsService, TagsMachine, TContextTag, TContext, TEvent, TState, Task, TagState } from './neve-measurements.interface';
import { cancel, pure } from 'xstate/lib/actions';
import { arraysEqual } from '~app/shared/utils';
import _ from 'lodash';

/**
 *
 * Provides a way to subscribe to tag events using child tag services
 * Makes sure all tags are refreshed at configured intervals and ensures
 * that there is ever only one request pending at a time.
 *
  Limitations of current implementation:
  - no tags can be removed, values will get updated indefinitely
  - global observable for all or a group of tag values
  - due to API calls being synchronous computations will need to use values with different timestamps
  - a hardcoded refresh rate is used for all tags
 */
@Injectable({
  providedIn: NeveMeasurementsModule
})
export class NeveMeasurementsService {
  public tagsService: TagsService;
  private readonly RATE_LIMIT_MS_DEFAULT = 1000;

  constructor(
    private readonly tagService: NeveMeasurementsTagService) {
  }

  /**
   * Initialize the state machine _if it hasn't been initialized before_.
   * `tagpairs` are passed into already existing machines,
   * but the `rateLimitMs` is only applied on the first init.
   */
  public async init(tagpairs?: TagPair[], rateLimitMs?: number) {
    if (!this.tagsService) {
      const tagsMachine: TagsMachine = this.createTagsMachine(rateLimitMs ? { rateLimitMs } : {});
      this.tagsService = interpret(tagsMachine);
      this.tagsService.start();
    }
    if (tagpairs?.length > 0) {
      await Promise.all(tagpairs.map(tagpair => this.createNewTag(tagpair)));
    }
  }

  // primarily for tests
  public stop() {
    if (this.tagsService) {
      this.tagsService.stop();
    }
  }

  public tagState$(tagpair: TagPair): Observable<TagState> {
    return this.tagEvents$(tagpair).pipe(
      scan<TagEvent, TagState>((acc, event) => {
        switch (event.type) {
          case 'REQUESTED_TAG_METADATA':
            const { requestedTag } = event;
            acc.metadata = {
              requestedTag
            };
            break;
          case 'RECEIVED_TAG_METADATA':
            acc.metadata = event.metadata;
            break;
          case 'RECEIVED_TAG_MEASUREMENTS':
            acc.status = 'loaded';
            acc.measurements = event.measurements.Values;
            break;
          case 'TAG.ERROR':
            acc.status = 'error';
            break;
        }
        return acc;
      }, {
        status: 'loading',
      }),
    );
  }

  private findTag(tagpair: TagPair): TContextTag {
    return this.tagsService.state.context.tags.get(getFullTag(tagpair));
  }

  /**
   * Creates an observable of cached values from latest events by type
   */
  private createCachedObservable(context: TagServiceContext): Observable<TagEvent> {
    const { error, metadata, measurements } = context.latestEvents;
    return from([error, metadata, measurements]).pipe(
      filter(event => !!event),
      map(event => ({
        ...event,
        cached: true
      })),
    );
  }

  private createEventsObservable(context: TagServiceContext, useCache: boolean): Observable<TagEvent> {
    return useCache
      ? concat(this.createCachedObservable(context), context.events$)
      : context.events$;
  }

  /**
   * Current implementation shouldn't fail when creating a tag,
   * in the future errors would need to be handled
   * if the implementation changes at some point
   */
  private waitForTagCreated(tagpair: TagPair): Promise<TContextTag> {
    return new Promise((resolve, reject) => {
      setTimeout(() => reject('timeout'), 10_000);
      // probably an anti-pattern?
      // would be nicer if we sent a callback that was called after initialize has been done?
      const sub = this.tagsService.subscribe(state => {
        if (state.event.type !== 'TAG.INITIALIZED' || !arraysEqual(state.event.context.config.requestedTag.tagPair, tagpair)) {
          return;
        }
        sub.unsubscribe();
        resolve(this.findTag(tagpair));
      });
    });
  }

  private createNewTag(tagpair: TagPair): Promise<TContextTag> {
    const existingTag = this.findTag(tagpair);
    if(existingTag) {
      return Promise.resolve(existingTag);
    }

    if(this.tagsService.state.context.pendingTags.has(getFullTag(tagpair))) {
      return this.waitForTagCreated(tagpair);
    }

    const tagCreatedPromise = this.waitForTagCreated(tagpair);
    this.tagsService.send({
      type: 'TAGS.CREATE',
      tagpairs: [tagpair]
    });
    return tagCreatedPromise;
  }

  public tagEvents$(tagpair: TagPair, useCache: boolean = true): Observable<TagEvent> {
    return from(this.createNewTag(tagpair)).pipe(
      // Requires actor to have been spawned with 'sync' option:
      // "Never read an actor's .state when sync is disabled; otherwise, you will end up referencing stale state."
      switchMap(tag => this.createEventsObservable(tag.ref.state.context, useCache)),
    );
  }

  public pickNextTask(tags: TContextTag[]): Task | undefined {
    // desperate attempt to stop a broken tag from blocking all other tags
    tags = _.shuffle(tags);

    type TaskType = {
      type: 'metadata' | 'measurements',
      tags: TContextTag[]
    };

    // convert last measurement request timestamp to next update timestamp
    const getMeasurementRefreshTime = (context: TagServiceContext) => {
      const latestMeasurementTimestampOrNow = context.requestTimestamps.measurements ?? new Date();
      return latestMeasurementTimestampOrNow.getTime() + context.config.measurementRefreshIntervalMs;
    };


    const tagsPartitionedByMetadataErrors: [TContextTag[], TContextTag[]] = _.partition(tags, (tag: TContextTag) => {
        const error = tag.ref?.state.context.latestEvents?.error;
        if(!error) {
          return false;
        }
        return !tag.ref.state.context.latestEvents.metadata && error.stage === 'metadata' && error.type === 'TAG.ERROR';
      });

      const tagsWithMetadataErrors = tagsPartitionedByMetadataErrors[0];
      const tagsWithoutMetadataErrors = tagsPartitionedByMetadataErrors[1];


      // will prioritize tags with no metadata yet fetched
    const tagsWithMetadataNotFetched: TaskType = {
      type: 'metadata',
      tags: tagsWithoutMetadataErrors.filter(tag => !tag.ref.state.context.latestEvents?.metadata)
    };

      // all tags have fetched metadata, find next measurement task
    const tagsWithMeasurementsNotFetched: TaskType = {
      type: 'measurements',
      tags: tagsWithoutMetadataErrors.filter(tag => tag.ref.state.context.latestEvents?.metadata && !tag.ref.state.context.latestEvents?.measurements)
    };

      // all tags have fetched measurements, find next one
    const tagsSortedByLastMeasurementRequest: TaskType = {
      type: 'measurements',
      tags: [ ...tagsWithoutMetadataErrors ].sort( (a, b) => {
        const getTs = (tag: TContextTag) => getMeasurementRefreshTime(tag.ref.state.context);
        return getTs(a) - getTs(b);
      })
    };

    const decideBetweenMetadataErrorOrMeasurementTags = (): TaskType => {
      if(tagsWithMetadataErrors.length === 0) {
          return tagsSortedByLastMeasurementRequest;
        }

      if(tagsSortedByLastMeasurementRequest.tags.length > 0) {
        const nextMeasurementTag = tagsSortedByLastMeasurementRequest.tags[0];
          const diffUntilRefresh = new Date(getMeasurementRefreshTime(nextMeasurementTag.ref.state.context)).getTime() - new Date().getTime();

        if (diffUntilRefresh < 5_000) {
          return tagsSortedByLastMeasurementRequest;
          }
        }
      // no measurements queued or there is still enough time, keep retrying metadata
      return {
        type: 'metadata',
        tags: tagsWithMetadataErrors
      };
    }

    const allTasksPrioritized: TaskType[] = [].concat(
        tagsWithMetadataNotFetched,
        tagsWithMeasurementsNotFetched,
        decideBetweenMetadataErrorOrMeasurementTags()
    ).filter(task => task.tags.length > 0);

    const chosenTask = allTasksPrioritized[0];
    if(!chosenTask) {
      return;
    }

    const firstTag = chosenTask.tags[0];
    if(!firstTag) {
      return;
    }

    const { context } = firstTag.ref.state;

    if (chosenTask.type === 'measurements') {
      return {
        when: context.latestEvents.measurements ? new Date(getMeasurementRefreshTime(context)) : new Date(),
        tag: firstTag
      };
    } else if(chosenTask.type === 'metadata') {
      return {
        when: new Date(),
        tag: firstTag
      }
    }
  }

  public calculateRateLimitedDelayMs({ minimumDelayMs, nextTask, previousTask, now }: { minimumDelayMs: number; nextTask: Task; previousTask?: Task; now?: Date }) {
    if (!previousTask) {
      // no delay needed
      return 0;
    }

    // if the previous task result came from cache in the backend
    // we are able to bypass the rate limit as the external Valmet DNA API was not called
    if(previousTask.cached) {
      minimumDelayMs = 0;
    }

    const difference = nextTask.when.getTime() - (now ?? new Date()).getTime();
    if (difference < 0) {
      const absDifference = Math.abs(difference);
      // task was scheduled to be run in the past
      if (absDifference < minimumDelayMs) {
        return minimumDelayMs - absDifference;
      }
      return 0;
    }

    return difference < minimumDelayMs ? minimumDelayMs : difference;
  }

  public createTagsMachine(contextOverrides: Partial<TContext> = {}): TagsMachine {
    const TASK_TIMER_ID = 'taskTimerId';

    return createMachine<TContext, TEvent, TState>({
      id: 'tags',
      context: {
        tags: new Map(),
        pendingTags: new Map(),
        rateLimitMs: this.RATE_LIMIT_MS_DEFAULT,
        ...contextOverrides,
      },
      initial: 'idle',
      // why does type safety not work for states here?
      // this has been investigated a bit but no solution found yet.
      // see https://xstate.js.org/docs/guides/typescript.html#typestates
      states: {
        'idle': {
          on: {
            'TAGS.CREATED': 'choosing-task',
          }
        },
        'choosing-task': {
          onEntry: [
            // cancel previous scheduled task if one exists
            cancel(TASK_TIMER_ID),
            /**
             * Pick next task and assign to context
             */
            assign({
              tasks: (context, event) => {
                return {
                  ...context.tasks,
                  next: this.pickNextTask(Array.from(context.tags.values()))
                }
              }
            }),
            /**
             * If there is a next task, send tag refresh event for it after an appropriate delay
             */
            pure((context, event) => {
              // with current implementation, this should not happen as
              // currently no tags are ever removed and tags update forever
              // if implementation changes to support tag removal, this should be handled
              if (!context.tasks?.next) {
                throw new Error('no next task');
              }

              return send({
                type: 'TAG.REFRESH',
                tag: context.tasks.next
              }, {
                delay: this.calculateRateLimitedDelayMs({ minimumDelayMs: context.rateLimitMs, nextTask: context.tasks.next, previousTask: context.tasks.previous }),
                id: TASK_TIMER_ID
              });
            })
          ],
          on: {
            // if new tags are created while waiting, choose again
            'TAGS.CREATED': 'choosing-task',
            'TAG.REFRESH': 'running-task'
          }
        },
        'running-task': {
          entry: pure((context, event) => {
              return send({
                type: 'REFRESH',
              }, {
                to: context.tasks.next.tag.ref.id,
              });
            }),
          exit: [
            assign((context, event) => {
              let cached: boolean;
              switch(event.type) {
                case 'RECEIVED_TAG_METADATA':
                  cached = event.metadata.tagsResult['cached-by-backend'];
                  break;
                case 'RECEIVED_TAG_MEASUREMENTS':
                  cached = event.measurements['cached-by-backend'];
                  break;
                default:
                  return context;
              }

              return {
                ...context,
                tasks: {
                  ...context.tasks,
                  next: {
                    ...context.tasks.next,
                    cached,
                  }
                }
              };
            }),
            assign({
              tasks: (context, event) => ({
                next: undefined,
                previous: context.tasks.next
              })
            }),
          ],
          on: {
            // will simply retry the task later if there are errors
            'TAG.ERROR': {
              target: 'choosing-task'
            },
            'RECEIVED_TAG_METADATA': {
              target: 'choosing-task'
            },
            'RECEIVED_TAG_MEASUREMENTS': {
              target: 'choosing-task'
            },
          }
        }
      },
      on: {
        'TAGS.CREATED': {},
        // sent from child
        'TAG.INITIALIZED': {
          actions: [
            assign<TContext, TagInitializedEvent>((context, event) => {
              const tags = new Map(context.tags);
              const pendingTags = new Map(context.pendingTags);

              const fullTag = event.context.config.requestedTag.fullTag;
              const tag = pendingTags.get(fullTag);
              if (!tag) {
                throw new Error(`pendingTag not found for ${fullTag}`);
              }

              tags.set(fullTag, tag);
              pendingTags.delete(fullTag);

              return { tags, pendingTags };
            }),
            send((context, event) => ({
              type: 'TAGS.CREATED',
              tagpairs: [event.context.config.requestedTag.fullTag],
            })),
          ]
        },
        // https://codesandbox.io/s/33wr94qv1?from-embed=&file=/todosMachine.js
        'TAGS.CREATE': {
          actions: [
            // spawn new tag machines for each new tag and initialize them, then assign (save) them to context
            assign({
              pendingTags: (context, event) => new Map([
                ...context.pendingTags,
                ...event.tagpairs.filter(
                  // prevent creating duplicates
                  tagpair => !context.pendingTags.has(getFullTag(tagpair)) && !context.tags.has(getFullTag(tagpair))
                ).map(tagpair => [
                  getFullTag(tagpair),
                  {
                    tagpair,
                    ref: spawn(this.tagService.createNewTagMachine(tagpair), {
                      name: getFullTag(tagpair),
                      sync: true
                    })
                  }
                ] as const)
              ])
            })
          ]
        }
      }
    });
  }


}
